import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Package, UploadSimple, DownloadSimple, FileXls, Sliders, CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight } from "@phosphor-icons/react";

// Fallback groups if the master is completely empty
const DEFAULT_GROUPS = [
  "DISPENSER", "TANK & PIPING", "AIR COMPRESSOR", "AIR BALANCER",
  "ACCESSORIES", "HOIST", "MACHINE", "TOOLS & EQUIPMENTS",
];

export default function PartsPage() {
  const { user } = useAuth();
  const isSupervisor = user.role === "supervisor";
  const canAdjustStock = ["supervisor", "manager"].includes(user.role);

  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editing, setEditing] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 3000 };
      if (groupFilter !== "all") params.group = groupFilter;
      const [it, gr] = await Promise.all([
        api.get("/stock", { params }),
        api.get("/stock/groups"),
      ]);
      setItems(it.data);
      setGroups(gr.data.map((g) => g.name));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [groupFilter]);

  const exportCsv = async () => {
    try {
      const params = {};
      if (groupFilter !== "all") params.group = groupFilter;
      if (search.trim()) params.q = search.trim();
      const res = await api.get("/stock/export.csv", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `flowfuel-parts-${Date.now()}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filename}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportXlsx = async () => {
    try {
      const params = {};
      if (groupFilter !== "all") params.group = groupFilter;
      if (search.trim()) params.q = search.trim();
      const res = await api.get("/stock/export.xlsx", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `flowfuel-parts-${Date.now()}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filename}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => !q || `${s.item} ${s.description}`.toLowerCase().includes(q));
  }, [items, search]);

  // Pagination window
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize],
  );
  // Reset to page 1 when filter/search/page-size changes
  useEffect(() => { setPage(1); }, [search, groupFilter, pageSize]);

  const groupOptions = useMemo(
    () => (groups.length ? groups : DEFAULT_GROUPS),
    [groups],
  );

  return (
    <div className="min-h-screen">
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10 no-print">
        <div className="px-10 py-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow">Master catalogue</div>
            <h1 className="font-display text-4xl tracking-tight leading-none mt-2">Parts</h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-xl">
              The FlowFuel master parts list. Every issue, return and request pulls from these codes.
              {isSupervisor
                ? " You can add, rename, edit or remove any part."
                : " Add a part here whenever you receive something new that isn't in the list."}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={exportXlsx}
              data-testid="export-parts-xlsx-btn"
              variant="outline"
              className="h-10 rounded-none border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white"
              title="Download every filtered part as formatted Excel (freeze pane, autofilter, currency)"
            >
              <FileXls size={16} weight="bold" className="mr-2" /> Download Excel
            </Button>
            <Button
              onClick={exportCsv}
              data-testid="export-parts-csv-btn"
              variant="outline"
              className="h-10 rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white"
              title="Download every filtered part as CSV"
            >
              <DownloadSimple size={16} weight="bold" className="mr-2" /> Download CSV
            </Button>
            {isSupervisor && (
              <Button
                onClick={() => setShowBulk(true)}
                data-testid="bulk-receive-btn"
                variant="outline"
                className="h-10 rounded-none border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white"
                title="Bulk-add received stock from a CSV or XLSX file"
              >
                <UploadSimple size={16} weight="bold" className="mr-2" /> Bulk Receive
              </Button>
            )}
            <Button
              onClick={() => setShowCreate(true)}
              data-testid="new-part-btn"
              className="h-10 rounded-none bg-neutral-900 text-neutral-50 hover:bg-neutral-800"
            >
              <Plus size={16} weight="bold" className="mr-2" /> Add Part
            </Button>
          </div>
        </div>
        <div className="px-10 pb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative col-span-2">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search item code or description…" className="h-10 pl-9 rounded-none border-neutral-300" data-testid="parts-search" />
          </div>
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger data-testid="parts-group-filter" className="h-10 rounded-none border-neutral-300"><SelectValue placeholder="Group" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All groups ({items.length})</SelectItem>
              {groupOptions.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-xs text-neutral-500 flex items-center gap-2 justify-end">
            <Package size={14} weight="bold" /> {filtered.length.toLocaleString()} match{filtered.length === 1 ? "" : "es"} · Page {currentPage} of {totalPages}
          </div>
        </div>
      </div>

      <div className="p-10">
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="parts-table">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="text-left">
                <th className="px-3 py-3 label-eyebrow">Item</th>
                <th className="px-3 py-3 label-eyebrow">Description</th>
                <th className="px-3 py-3 label-eyebrow">Group</th>
                <th className="px-3 py-3 label-eyebrow">UOM</th>
                <th className="px-3 py-3 label-eyebrow text-right">On Hand</th>
                <th className="px-3 py-3 label-eyebrow text-right text-red-700">Faulty Stock</th>
                <th className="px-3 py-3 label-eyebrow no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">No parts match your filters. {isSupervisor || user.role === "storekeeper" ? "Click 'Add Part' to create one." : ""}</td></tr>
              )}
              {pageItems.map((s) => (
                <tr key={s.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`part-row-${s.id}`}>
                  <td className="px-3 py-2 font-medium text-xs">{s.item}</td>
                  <td className="px-3 py-2 text-xs text-neutral-600">{s.description}</td>
                  <td className="px-3 py-2"><span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-neutral-100 text-neutral-700">{s.group}</span></td>
                  <td className="px-3 py-2 text-xs text-neutral-500 font-mono">{s.uom}</td>
                  <td className={`px-3 py-2 text-right font-mono ${(s.on_hand_qty || 0) === 0 ? "text-red-700 font-semibold" : ""}`}>{s.on_hand_qty || 0}</td>
                  <td
                    className={`px-3 py-2 text-right font-mono ${(s.faulty_qty || 0) > 0 ? "text-red-700 font-semibold" : "text-neutral-400"}`}
                    data-testid={`part-faulty-qty-${s.id}`}
                    title="Used + Faulty parts sitting in the faulty-stock bucket. Never sellable."
                  >
                    {s.faulty_qty || 0}
                  </td>
                  <td className="px-3 py-2 no-print">
                    {(isSupervisor || canAdjustStock) ? (
                      <div className="flex items-center gap-1.5">
                        {canAdjustStock && (
                          <button onClick={() => setAdjusting(s)} className="px-2 py-1 border border-amber-500 text-amber-800 hover:bg-amber-500 hover:text-white flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider" title="Increase or decrease on-hand qty (with reason)" data-testid={`adjust-part-${s.id}`}>
                            <Sliders size={12} weight="bold" /> Adjust
                          </button>
                        )}
                        {isSupervisor && (
                          <>
                            <button onClick={() => setReceiving(s)} className="px-2 py-1 border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider" title="Receive shipment (add to on-hand)" data-testid={`receive-part-${s.id}`}>
                              <Package size={12} weight="bold" /> Receive
                            </button>
                            <button onClick={() => setEditing(s)} className="px-2 py-1 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider" title="Edit item metadata" data-testid={`edit-part-${s.id}`}>
                              <PencilSimple size={12} weight="bold" /> Edit
                            </button>
                            <button onClick={() => confirmDelete(s, load)} className="p-1.5 border border-neutral-300 hover:border-red-600 hover:bg-red-600 hover:text-white" title="Delete" data-testid={`delete-part-${s.id}`}>
                              <Trash size={14} weight="bold" />
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="text-neutral-400 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <Paginator
              page={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filtered.length}
              onPage={setPage}
              onPageSize={setPageSize}
            />
          )}
        </div>
      </div>

      {showCreate && (
        <PartDialog
          groups={groupOptions}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {editing && isSupervisor && (
        <PartDialog
          part={editing}
          groups={groupOptions}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {receiving && isSupervisor && (
        <ReceiveShipmentDialog
          part={receiving}
          onClose={() => setReceiving(null)}
          onSaved={() => { setReceiving(null); load(); }}
        />
      )}
      {adjusting && canAdjustStock && (
        <AdjustStockDialog
          part={adjusting}
          onClose={() => setAdjusting(null)}
          onSaved={() => { setAdjusting(null); load(); }}
        />
      )}
      {showBulk && isSupervisor && (
        <BulkReceiveDialog
          onClose={() => setShowBulk(false)}
          onSaved={() => { setShowBulk(false); load(); }}
        />
      )}
    </div>
  );
}

async function confirmDelete(part, refresh) {
  if (!window.confirm(`Delete part "${part.item}" from the master list?`)) return;
  try {
    await api.delete(`/stock/${part.id}`);
    toast.success(`Deleted ${part.item}`);
    refresh();
  } catch (e) { toast.error(formatApiError(e)); }
}

function PartDialog({ part, groups, onClose, onSaved }) {
  const isEdit = !!part;
  const [item, setItem] = useState(part?.item || "");
  const [description, setDescription] = useState(part?.description || "");
  const [group, setGroup] = useState(part?.group || (groups[0] || ""));
  const [customGroup, setCustomGroup] = useState("");
  const [uom, setUom] = useState(part?.uom || "UNIT");
  const [onHand, setOnHand] = useState(part?.on_hand_qty ?? 0);
  const [busy, setBusy] = useState(false);
  const showCustomGroup = group === "__new__";
  const finalGroup = showCustomGroup ? customGroup.trim().toUpperCase() : group;

  const submit = async () => {
    const code = item.trim();
    if (!code) return toast.error("Item code required");
    if (!finalGroup) return toast.error("Group required");
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/stock/${part.id}`, {
          item: code,
          description: description.trim() || code,
          group: finalGroup,
          uom: uom.trim() || "UNIT",
        });
      } else {
        await api.post("/stock", {
          item: code,
          description: description.trim() || code,
          group: finalGroup,
          uom: uom.trim() || "UNIT",
          on_hand_qty: Math.max(0, Number(onHand) || 0),
        });
      }
      toast.success(isEdit ? "Part updated" : `Added ${code}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-neutral-900" data-testid="part-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isEdit ? `Edit ${part.item}` : "Add New Part"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Item code / short name</Label>
            <Input value={item} onChange={(e) => setItem(e.target.value)} className="rounded-none border-neutral-400" data-testid="part-item" autoFocus />
            <p className="text-[10px] text-neutral-500 font-mono">Must be unique. Example: {"O'RING 010"}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Longer description (optional)" className="rounded-none border-neutral-400" data-testid="part-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Group</Label>
              <Select value={group} onValueChange={setGroup}>
                <SelectTrigger data-testid="part-group" className="rounded-none border-neutral-400"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  <SelectItem value="__new__">+ New group…</SelectItem>
                </SelectContent>
              </Select>
              {showCustomGroup && (
                <Input value={customGroup} onChange={(e) => setCustomGroup(e.target.value)} placeholder="New group name (UPPERCASE)" className="mt-1 rounded-none border-neutral-400 font-mono uppercase text-xs" data-testid="part-custom-group" />
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">UOM</Label>
              <Input value={uom} onChange={(e) => setUom(e.target.value)} className="rounded-none border-neutral-400 font-mono" data-testid="part-uom" />
              <p className="text-[10px] text-neutral-500 font-mono">Unit of measure — UNIT, SET, MTR, LTR…</p>
            </div>
          </div>
          {!isEdit && (
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Opening stock qty (optional)</Label>
              <Input type="number" min={0} value={onHand} onChange={(e) => setOnHand(e.target.value)} className="rounded-none border-neutral-400 font-mono" data-testid="part-on-hand" />
              <p className="text-[10px] text-neutral-500 font-mono">Set the initial physical qty on hand. Leave 0 if you&apos;ll intake later.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="part-submit" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            {busy ? "Saving…" : (isEdit ? "Save Changes" : "Add Part")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveShipmentDialog({ part, onClose, onSaved }) {
  const [units, setUnits] = useState("");
  const [ref, setRef] = useState("");
  const [supplier, setSupplier] = useState("");
  const [busy, setBusy] = useState(false);

  const newTotal = (part.on_hand_qty || 0) + (Number(units) || 0);
  const submit = async () => {
    const n = Number(units);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Enter a positive number of units received");
    setBusy(true);
    try {
      await api.post(`/stock/${part.id}/receive`, {
        units: n,
        reference: ref.trim() || null,
        supplier: supplier.trim() || null,
      });
      toast.success(`Received ${n} × ${part.item} → on-hand now ${newTotal}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none border-neutral-900" data-testid="receive-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Package size={22} weight="duotone" className="text-emerald-700" />
            Receive Shipment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-neutral-200 bg-neutral-50 p-3 space-y-1">
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{part.group} · {part.uom}</div>
            <div className="text-sm font-medium">{part.item}</div>
            {part.description && part.description !== part.item && (
              <div className="text-xs text-neutral-500">{part.description}</div>
            )}
            <div className="pt-2 flex items-baseline gap-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">On hand now</span>
              <span className="font-mono text-lg">{part.on_hand_qty || 0}</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Units received</Label>
            <Input
              type="number" min={1}
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              placeholder="e.g. 50"
              className="rounded-none border-neutral-400 font-mono text-lg h-12"
              data-testid="receive-units"
              autoFocus
            />
            {Number(units) > 0 && (
              <p className="text-xs text-emerald-700 font-mono">
                {part.on_hand_qty || 0} + {Number(units)} = <b>{newTotal}</b> on-hand after this shipment
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">PO / Ref. no (optional)</Label>
              <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="PO-2026-042" className="rounded-none border-neutral-400 font-mono" data-testid="receive-reference" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Supplier (optional)</Label>
              <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="ABC Trading" className="rounded-none border-neutral-400" data-testid="receive-supplier" />
            </div>
          </div>

          <div className="text-[10px] text-neutral-500 font-mono">
            Adds units to on-hand and writes an audit-log entry (`stock.receive`).
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy || !Number(units)} data-testid="receive-submit" className="rounded-none bg-emerald-700 text-white hover:bg-emerald-800">
            {busy ? "Recording…" : "Add to inventory"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function BulkReceiveDialog({ onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [reference, setReference] = useState("");
  const [supplier, setSupplier] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) return toast.error("Choose a CSV or XLSX file first");
    const fd = new FormData();
    fd.append("file", file);
    if (reference.trim()) fd.append("reference", reference.trim());
    if (supplier.trim()) fd.append("supplier", supplier.trim());
    setBusy(true);
    try {
      const { data } = await api.post("/stock/bulk-receive", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const msg = `Received ${data.total_units} units across ${data.updated_count} item(s)`
        + (data.unmatched_count ? ` · ${data.unmatched_count} unmatched` : "");
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const done = () => { onSaved(); };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && (result ? done() : onClose())}>
      <DialogContent className="max-w-2xl rounded-none border-neutral-900 max-h-[92vh] overflow-y-auto" data-testid="bulk-receive-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <UploadSimple size={22} weight="duotone" className="text-emerald-700" />
            Bulk Receive Shipment
          </DialogTitle>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600 space-y-1">
              <div className="label-eyebrow">Expected columns (case-insensitive)</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li><span className="font-mono">item</span> (or <span className="font-mono">code</span>, <span className="font-mono">sku</span>, <span className="font-mono">part_no</span>) — <b>required</b>, must match master list</li>
                <li><span className="font-mono">units</span> (or <span className="font-mono">qty</span>, <span className="font-mono">quantity</span>) — <b>required</b>, positive integer</li>
                <li><span className="font-mono">reference</span> and <span className="font-mono">supplier</span> — optional, per-row overrides</li>
              </ul>
              <div className="text-[10px] font-mono text-neutral-500 pt-1">CSV or XLSX · max 5 MB</div>
            </div>

            <div className="space-y-1.5">
              <Label className="label-eyebrow">Shipment file</Label>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="rounded-none border-neutral-400"
                data-testid="bulk-receive-file"
              />
              {file && (
                <p className="text-[10px] text-neutral-500 font-mono">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Default PO / Ref. (optional)</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="PO-2026-042" className="rounded-none border-neutral-400 font-mono" data-testid="bulk-receive-reference" />
                <p className="text-[10px] text-neutral-500 font-mono">Used when a row has no reference column.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Default supplier (optional)</Label>
                <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="ABC Trading" className="rounded-none border-neutral-400" data-testid="bulk-receive-supplier" />
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-emerald-300 bg-emerald-50 p-3">
                <div className="label-eyebrow text-emerald-700">Updated</div>
                <div className="font-display text-3xl text-emerald-800" data-testid="bulk-result-updated">{result.updated_count}</div>
                <div className="text-[10px] font-mono text-emerald-700">items</div>
              </div>
              <div className="border border-neutral-300 bg-neutral-50 p-3">
                <div className="label-eyebrow">Total units</div>
                <div className="font-display text-3xl">{result.total_units}</div>
              </div>
              <div className={`border p-3 ${result.unmatched_count ? "border-amber-300 bg-amber-50" : "border-neutral-200 bg-neutral-50"}`}>
                <div className={`label-eyebrow ${result.unmatched_count ? "text-amber-700" : ""}`}>Unmatched</div>
                <div className={`font-display text-3xl ${result.unmatched_count ? "text-amber-800" : "text-neutral-400"}`} data-testid="bulk-result-unmatched">{result.unmatched_count}</div>
                <div className="text-[10px] font-mono">rows</div>
              </div>
            </div>

            {result.updated_count > 0 && (
              <div className="border border-neutral-200 max-h-56 overflow-y-auto">
                <table className="w-full text-xs" data-testid="bulk-result-updated-table">
                  <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0">
                    <tr className="text-left">
                      <th className="px-2 py-1.5 label-eyebrow">Item</th>
                      <th className="px-2 py-1.5 label-eyebrow text-right">+Units</th>
                      <th className="px-2 py-1.5 label-eyebrow text-right">Before → After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.updated.map((r) => (
                      <tr key={r.id} className="border-b border-neutral-100 last:border-0">
                        <td className="px-2 py-1.5 font-mono">{r.item}</td>
                        <td className="px-2 py-1.5 font-mono text-right text-emerald-700">+{r.units}</td>
                        <td className="px-2 py-1.5 font-mono text-right text-neutral-600">{r.before} → <b className="text-neutral-900">{r.after}</b></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {result.unmatched_count > 0 && (
              <div className="border border-amber-300 bg-amber-50 max-h-40 overflow-y-auto">
                <div className="px-3 py-1.5 label-eyebrow text-amber-700 border-b border-amber-200 bg-amber-100">Unmatched rows — item codes not in master list</div>
                <table className="w-full text-xs" data-testid="bulk-result-unmatched-table">
                  <tbody>
                    {result.unmatched.map((r, i) => (
                      <tr key={i} className="border-b border-amber-200 last:border-0">
                        <td className="px-2 py-1.5 font-mono">{r.item}</td>
                        <td className="px-2 py-1.5 font-mono text-right text-amber-800">{r.units} units</td>
                        <td className="px-2 py-1.5 text-amber-700 italic">{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
              <Button onClick={submit} disabled={busy || !file} data-testid="bulk-receive-submit" className="rounded-none bg-emerald-700 text-white hover:bg-emerald-800">
                {busy ? "Uploading…" : "Upload & Receive"}
              </Button>
            </>
          ) : (
            <Button onClick={done} data-testid="bulk-receive-done" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------------------------------
// Paginator — page tabs + size selector for large lists (2000+ parts).
// Shows: [First] [Prev] 1 … 4 5 [6] 7 8 … 22 [Next] [Last]  · 100 per page
// -------------------------------------------------------------------------
function Paginator({ page, totalPages, pageSize, totalItems, onPage, onPageSize }) {
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);

  // Build a compact page number list with ellipses.
  const pages = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const out = new Set([1, totalPages, page]);
    for (let d = 1; d <= 2; d++) { out.add(page - d); out.add(page + d); }
    const sorted = [...out].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const withGaps = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) withGaps.push("…");
      withGaps.push(sorted[i]);
    }
    return withGaps;
  }, [page, totalPages]);

  const IconBtn = ({ onClick, disabled, testid, title, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className="h-8 w-8 border border-neutral-300 flex items-center justify-center text-neutral-700 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-neutral-400"
    >
      {children}
    </button>
  );

  return (
    <div className="border-t border-neutral-200 px-3 py-2 flex flex-wrap items-center justify-between gap-3" data-testid="parts-paginator">
      <div className="text-xs text-neutral-500 font-mono">
        {totalItems === 0
          ? "0 items"
          : <>Showing <b className="text-neutral-900">{start.toLocaleString()}</b>–<b className="text-neutral-900">{end.toLocaleString()}</b> of <b className="text-neutral-900">{totalItems.toLocaleString()}</b></>}
      </div>
      <div className="flex items-center gap-1">
        <IconBtn onClick={() => onPage(1)}          disabled={page === 1}          testid="page-first" title="First page"><CaretDoubleLeft  size={12} weight="bold" /></IconBtn>
        <IconBtn onClick={() => onPage(page - 1)}   disabled={page === 1}          testid="page-prev"  title="Previous page"><CaretLeft        size={12} weight="bold" /></IconBtn>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`gap-${i}`} className="px-1 text-xs text-neutral-400 font-mono">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPage(p)}
              data-testid={`page-${p}`}
              className={`h-8 min-w-8 px-2 border text-xs font-mono ${
                p === page
                  ? "border-neutral-900 bg-neutral-900 text-white"
                  : "border-neutral-300 text-neutral-700 hover:bg-neutral-100"
              }`}
            >
              {p}
            </button>
          ),
        )}
        <IconBtn onClick={() => onPage(page + 1)}   disabled={page === totalPages} testid="page-next"  title="Next page"><CaretRight        size={12} weight="bold" /></IconBtn>
        <IconBtn onClick={() => onPage(totalPages)} disabled={page === totalPages} testid="page-last"  title="Last page"><CaretDoubleRight  size={12} weight="bold" /></IconBtn>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-neutral-500">
        <span className="label-eyebrow">Per page</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          data-testid="page-size"
          className="h-8 border border-neutral-300 bg-white text-xs font-mono px-2 hover:border-neutral-900"
        >
          {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );
}


// -------------------------------------------------------------------------
// AdjustStockDialog — supervisor/manager can bump on-hand qty up or down
// with a mandatory reason (theft, damage, physical count reconciliation…).
// Uses PATCH /stock/{id}/qty which sets the ABSOLUTE new qty.
// -------------------------------------------------------------------------
function AdjustStockDialog({ part, onClose, onSaved }) {
  const before = Number(part.on_hand_qty || 0);
  const [mode, setMode] = useState("delta"); // "delta" | "absolute"
  const [delta, setDelta] = useState("");
  const [absolute, setAbsolute] = useState(String(before));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const parsed = mode === "delta" ? Number(delta) : Number(absolute);
  const after = mode === "delta" ? before + (Number(delta) || 0) : (Number(absolute) || 0);
  const diff = after - before;
  const valid =
    mode === "delta"
      ? Number.isFinite(parsed) && parsed !== 0 && after >= 0
      : Number.isFinite(parsed) && parsed >= 0 && parsed !== before;

  const submit = async () => {
    if (!valid) return toast.error("Enter a valid change");
    if (!reason.trim()) return toast.error("A reason is required");
    if (after < 0) return toast.error("On-hand qty can't go below zero");
    setBusy(true);
    try {
      await api.patch(`/stock/${part.id}/qty`, { on_hand_qty: after, reason: reason.trim() });
      toast.success(`${part.item}: ${before} → ${after} (${diff >= 0 ? "+" : ""}${diff})`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none border-neutral-900" data-testid="adjust-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Sliders size={22} weight="duotone" className="text-amber-600" />
            Adjust Stock — {part.item}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 border border-neutral-300">
            <div className="p-3 border-r border-neutral-300 text-center">
              <div className="label-eyebrow">Before</div>
              <div className="font-mono text-2xl mt-1">{before}</div>
            </div>
            <div className="p-3 border-r border-neutral-300 text-center">
              <div className="label-eyebrow">Change</div>
              <div className={`font-mono text-2xl mt-1 ${diff > 0 ? "text-emerald-700" : diff < 0 ? "text-red-700" : "text-neutral-400"}`}>
                {valid && diff !== 0 ? `${diff > 0 ? "+" : ""}${diff}` : "—"}
              </div>
            </div>
            <div className="p-3 text-center">
              <div className="label-eyebrow">After</div>
              <div className={`font-mono text-2xl mt-1 ${after < 0 ? "text-red-700" : "text-neutral-900"}`}>{valid ? after : before}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode("delta")}
              data-testid="adjust-mode-delta"
              className={`h-10 border-2 text-xs font-mono uppercase tracking-wider transition-colors ${
                mode === "delta"
                  ? "border-amber-500 bg-amber-500 text-white"
                  : "border-neutral-300 bg-white hover:border-amber-500"
              }`}
            >
              +/− Adjust
            </button>
            <button
              type="button"
              onClick={() => setMode("absolute")}
              data-testid="adjust-mode-absolute"
              className={`h-10 border-2 text-xs font-mono uppercase tracking-wider transition-colors ${
                mode === "absolute"
                  ? "border-neutral-900 bg-neutral-900 text-white"
                  : "border-neutral-300 bg-white hover:border-neutral-900"
              }`}
            >
              Set new qty
            </button>
          </div>

          {mode === "delta" ? (
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Change by (use minus for decrease)</Label>
              <Input
                type="number"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                placeholder="e.g. -5 or 12"
                className="rounded-none border-neutral-400 font-mono h-11 text-lg"
                data-testid="adjust-delta"
                autoFocus
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="label-eyebrow">New on-hand qty</Label>
              <Input
                type="number"
                min={0}
                value={absolute}
                onChange={(e) => setAbsolute(e.target.value)}
                className="rounded-none border-neutral-400 font-mono h-11 text-lg"
                data-testid="adjust-absolute"
                autoFocus
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Reason (required)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. physical count reconciliation, damaged in transit, theft"
              className="rounded-none border-neutral-400"
              data-testid="adjust-reason"
            />
            <div className="text-[10px] text-neutral-500 font-mono">Saved to Audit Log as <span className="uppercase">stock.adjust</span>.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !valid || !reason.trim()}
            data-testid="adjust-submit"
            className="rounded-none bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
          >
            {busy ? "Saving…" : `Apply · ${diff >= 0 ? "+" : ""}${valid ? diff : 0}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

